import { Link } from 'react-router-dom';
import { services } from '@/mocks/tmtData';
import { useTiltEffect } from '@/hooks/useTiltEffect';
import { useScrollReveal } from '@/hooks/useScrollReveal';

const fadeIn = (visible: boolean, delay: string) => ({
  opacity: visible ? 1 : 0,
  transform: visible ? 'translateY(0)' : 'translateY(24px)',
  transition: 'all 700ms ease-out',
  transitionDelay: delay,
});

function ServiceCard({ service, index, visible }: { service: typeof services[0]; index: number; visible: boolean }) {
  const tilt = useTiltEffect({ maxTilt: 6, scale: 1.04, speed: 500 });

  return (
    <div
      className="group relative bg-background-100/90 border border-primary-700/30 rounded-2xl overflow-hidden hover:border-primary-500/50 transition-all duration-500"
      style={fadeIn(visible, `${index * 150}ms`)}
    >
      {/* Image Area */}
      <div
        ref={tilt.ref}
        onMouseMove={tilt.onMouseMove}
        onMouseLeave={tilt.onMouseLeave}
        className="relative h-56 md:h-64 overflow-hidden bg-background-50/80"
      >
        <img
          src={service.image}
          alt={service.title}
          style={tilt.style}
          className="w-full h-full object-cover transition-transform duration-300"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background-100 via-background-100/20 to-transparent" />
        <div className="absolute top-4 left-4 w-10 h-10 bg-primary-500/20 backdrop-blur-sm border border-primary-500/30 rounded-lg flex items-center justify-center">
          <i className={`${service.icon} text-primary-400 text-lg`} />
        </div>
      </div>

      {/* Content Area */}
      <div className="p-5 md:p-6">
        <h3 className="font-heading text-xl md:text-2xl text-foreground-100 mb-2 group-hover:text-primary-400 transition-colors">
          {service.title}
        </h3>
        <p className="text-foreground-400 text-sm leading-relaxed mb-4 font-body">
          {service.description}
        </p>
        <Link
          to="/services"
          className="inline-flex items-center gap-2 text-primary-400 text-sm font-label tracking-wider uppercase hover:text-primary-300 transition-colors"
        >
          Learn More
          <i className="ri-arrow-right-line" />
        </Link>
      </div>
    </div>
  );
}

export default function ServicesSection() {
  const [headerRef, headerVisible] = useScrollReveal<HTMLDivElement>({ threshold: 0.15 });
  const [gridRef, gridVisible] = useScrollReveal<HTMLDivElement>({ threshold: 0.08 });
  const featuredServices = services.slice(0, 3);

  return (
    <section className="bg-background-50/95 py-20 md:py-28 px-4 md:px-8 lg:px-12">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div ref={headerRef} className="mb-12 md:mb-16">
          <span
            className="inline-block font-label text-xs tracking-[0.2em] uppercase text-primary-400 mb-3"
            style={fadeIn(headerVisible, '0ms')}
          >
            / Our Services
          </span>
          <h2
            className="font-heading text-3xl sm:text-4xl md:text-5xl font-bold text-foreground-50 leading-tight max-w-2xl"
            style={fadeIn(headerVisible, '100ms')}
          >
            Premium Financial
            <br />
            <span className="text-primary-400">Solutions</span>
          </h2>
        </div>

        {/* Services Grid */}
        <div ref={gridRef} className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {featuredServices.map((service, i) => (
            <ServiceCard key={service.id} service={service} index={i} visible={gridVisible} />
          ))}
        </div>

        {/* View All Link */}
        <div className="mt-10 md:mt-14 text-center">
          <Link
            to="/services"
            className="inline-flex items-center gap-3 px-8 py-3.5 border border-primary-600/50 text-primary-400 font-label text-sm tracking-wider uppercase rounded-full hover:bg-primary-500 hover:text-background-50 hover:border-primary-500 transition-all duration-300"
          >
            View All Services
            <i className="ri-arrow-right-line" />
          </Link>
        </div>
      </div>
    </section>
  );
}